/**
 * Generic bulk-selection resolution, shared by any grid that offers
 * "select specific rows" vs "select all matching the current filter"
 * (SSRM header select-all + toggled exclusions).
 *
 * Extracted from the policy book's bulk-status selection handling
 * (`components/policy/policy-book/bulk-policy-status.ts`,
 * `resolvePolicyBookBulkSelection` / `buildBulkStatusFilter`) so other
 * entities (e.g. dialer leads) can reuse the same selection contract
 * instead of forking a one-off copy. The policy book keeps its existing
 * policy-typed wrappers; new bulk UIs should build directly on this file.
 */

/** Finite row picks vs header select-all (filter-scoped), entity-agnostic. */
export type BulkSelection =
  | { mode: 'none'; count: 0 }
  | { mode: 'ids'; ids: string[]; count: number }
  | { mode: 'filter'; excludedIds: string[]; count: number };

/**
 * Resolves an ag-grid SSRM selection state into bulk targets.
 * - Header select-all → filter path (server re-query), honoring toggled exclusions.
 * - Otherwise → explicit row ids (`toggledNodeIds` when present, else loaded rows).
 */
export function resolveBulkSelection(params: {
  selectAll: boolean;
  toggledNodeIds: string[];
  selectedRowIds: string[];
  matchedTotal: number;
}): BulkSelection {
  if (params.selectAll) {
    const excludedIds = [...new Set(params.toggledNodeIds.filter(Boolean))];
    const count = Math.max(0, params.matchedTotal - excludedIds.length);
    if (count <= 0) {
      return { mode: 'none', count: 0 };
    }
    return { mode: 'filter', excludedIds, count };
  }

  // When selectAll is false, SSRM toggledNodes are the selected row ids.
  const fromState = params.toggledNodeIds.filter(Boolean);
  const ids = [
    ...new Set(
      (fromState.length > 0 ? fromState : params.selectedRowIds).filter(Boolean)
    ),
  ];
  if (ids.length === 0) {
    return { mode: 'none', count: 0 };
  }
  return { mode: 'ids', ids, count: ids.length };
}

/**
 * Server contract for a bulk action target: either an explicit id list
 * (individual select) or a filter to re-run server-side plus any ids the
 * user toggled off after select-all (filter select-all).
 */
export type BulkActionTarget =
  | { mode: 'ids'; ids: string[] }
  | { mode: 'filter'; filter: Record<string, unknown>; excludedIds: string[] };

/** Builds the server request target from the current selection + filter. */
export function buildBulkActionTarget(params: {
  selection: BulkSelection;
  currentFilter: Record<string, unknown>;
}): BulkActionTarget | null {
  if (params.selection.mode === 'none') {
    return null;
  }
  if (params.selection.mode === 'ids') {
    return { mode: 'ids', ids: params.selection.ids };
  }
  return {
    mode: 'filter',
    filter: params.currentFilter,
    excludedIds: params.selection.excludedIds,
  };
}

/**
 * How many entities one bulk request may resolve to. Matches the schema
 * ceiling every bulk selection endpoint accepts (`maxLeads`/`maxPolicies`,
 * `maximum: 2000` in `leadBulk.ts`/`policyStatusBulk.ts`) — a selection over
 * this is refused outright rather than partially applied, so UIs must gate
 * on it before letting the user commit.
 *
 * "Select all matching filter" sends the server a query, not a list of ids,
 * so this is not a limit on how large a selection can be *expressed* — it is
 * a limit on how many per-item mutations one synchronous HTTP request can
 * resolve and act on before responding (see `executeFilteredBulk`'s
 * concurrency clamp of 50). Raising this constant alone changes nothing on
 * its own — a caller must also request that much capacity via `maxLeadsForCount`
 * (below), the same pattern the policy book's bulk owner/lead-list/workflow
 * actions already use (`Math.min(Math.max(selection.count, 1), 2000)` in
 * e.g. `bulk-policy-owner.component.ts`) instead of leaving `maxLeads`
 * unset and falling back to the server's conservative default (500).
 */
export const MAX_BULK_LEADS = 2000;

/** Whether a selection is small enough for the server to act on it. */
export function isBulkSelectionWithinLimit(count: number): boolean {
  return count <= MAX_BULK_LEADS;
}

/**
 * The `maxLeads`/`maxPolicies`/`maxCalls` value to request for a selection
 * of this size — exactly as much capacity as the resolved selection needs,
 * capped at what the server will ever accept. Passing this (instead of
 * omitting the field) is what actually unlocks selections above the
 * server's own conservative default of 500; see `MAX_BULK_LEADS`'s doc
 * comment.
 */
export function maxLeadsForCount(count: number): number {
  return Math.min(Math.max(count, 1), MAX_BULK_LEADS);
}

/**
 * Per-item concurrency to request from the server for a bulk write,
 * scaled with selection size. The default (10) is fine for small selections;
 * a "select all matching filter" run approaching `MAX_BULK_LEADS` items does
 * meaningfully more wall-clock work (one Firestore round trip or more per
 * item), so ask the server to run more of them in parallel — it clamps to 50
 * regardless (`executeFilteredBulk`), so this can never over-request.
 */
export function bulkConcurrencyForCount(count: number): number {
  if (count > 200) {
    return 50;
  }
  if (count > 50) {
    return 25;
  }
  return 10;
}

/**
 * Client-side request timeout for bulk action calls. `EnrollhereApiService`'s
 * default (30s) is sized for ordinary single-entity requests; a bulk write
 * over a large "select all matching filter" selection can legitimately take
 * longer even at raised concurrency, and letting the client abort while the
 * server keeps processing (or has already finished) leaves the user with a
 * misleading failure and no way to tell what actually happened. Bulk callers
 * should pass this as the request's timeout override.
 */
export const BULK_ACTION_REQUEST_TIMEOUT_MS = 120_000;

/**
 * Serializes a `BulkActionTarget` into the `selection` object every bulk lead
 * endpoint (`/dialer/bulk/lead-list`, `/dialer/bulk/lead-participants`,
 * `/dialer/bulk/lead-task`) takes. The server names the id list `leadIds`, not
 * `ids`, and requires the whole thing nested under `selection` — hand-rolling
 * this mapping per caller is how the bulk task call shipped a body the route
 * rejected outright, so new bulk clients should call this instead.
 */
export function toBulkSelectionBody(params: {
  target: BulkActionTarget;
  usePrivateLeads?: boolean;
  maxLeads?: number;
  concurrency?: number;
}): Record<string, unknown> {
  const base =
    params.target.mode === 'ids'
      ? { mode: 'ids', leadIds: params.target.ids }
      : {
          mode: 'filter',
          filter: params.target.filter,
          excludedIds: params.target.excludedIds,
        };
  return {
    ...base,
    usePrivateLeads: !!params.usePrivateLeads,
    ...(params.maxLeads === undefined ? {} : { maxLeads: params.maxLeads }),
    ...(params.concurrency === undefined
      ? {}
      : { concurrency: params.concurrency }),
  };
}

/** Shared "N selected things (matching the current filter)" summary label. */
export function bulkSelectionSummaryLabel(params: {
  count: number;
  singularNoun: string;
  pluralNoun: string;
  selectionMode: 'ids' | 'filter';
}): string {
  const countLabel = params.count.toLocaleString('en-US');
  const noun = params.count === 1 ? params.singularNoun : params.pluralNoun;
  return params.selectionMode === 'filter'
    ? `${countLabel} selected ${noun} matching the current filter`
    : `${countLabel} selected ${noun}`;
}

/**
 * Maps a `BulkActionTarget` onto the policy bulk endpoints' filter contract
 * (`/policy/status/bulk`, `/policy/owner/bulk`, `/policy/task/bulk`): the
 * policy side has no separate selection object, so explicit ids ride inside
 * the filter as `policy-id` and select-all exclusions as
 * `exclude-policy-id`.
 */
export function toPolicyBulkFilter(
  target: BulkActionTarget
): Record<string, unknown> {
  if (target.mode === 'ids') {
    return { 'policy-id': target.ids };
  }
  const filter: Record<string, unknown> = { ...target.filter };
  if (target.excludedIds.length > 0) {
    filter['exclude-policy-id'] = target.excludedIds;
  }
  return filter;
}

/**
 * Shape common to every bulk outcome row the server returns (lead-list,
 * lead-participants, lead/policy bulk task creation) — enough fields to
 * decide "did this one succeed" and, if not, to explain why to a person.
 */
export interface BulkResultRow {
  id: string;
  displayName?: string;
  outcome: string;
  skipReason?: string;
  error?: string;
}

/** One non-success row, ready to render in a results list. */
export interface BulkResultProblem {
  id: string;
  /** `displayName` when the server resolved one, otherwise the bare id. */
  label: string;
  reason: string;
  isError: boolean;
}

const BULK_SUCCESS_OUTCOMES = new Set(['updated', 'created', 'would-update']);

/**
 * Filters a bulk response's per-item results down to the ones a user should
 * actually look at, normalizing the lead/policy list-vs-task outcome
 * vocabularies into one shape. Aggregate counts (`totalSkipped`/
 * `totalFailed`) tell a caller *that* something needs attention; this is
 * what lets a UI show *what*, without a second lookup by id.
 */
export function toBulkResultProblems(
  results: readonly BulkResultRow[]
): BulkResultProblem[] {
  return results
    .filter(row => !BULK_SUCCESS_OUTCOMES.has(row.outcome))
    .map(row => ({
      id: row.id,
      label: row.displayName?.trim() || row.id,
      reason: row.error || row.skipReason || 'No reason given',
      isError: row.outcome === 'failed',
    }));
}
